import { useState } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { apiRequest } from "@/lib/queryClient";
import { useCustomer } from "@/lib/customer-context";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard,
  Plus,
  ArrowRight,
  TrendingUp,
  ArrowUpCircle,
  ArrowDownCircle,
} from "lucide-react";
import { Link } from "wouter";
import { BalanceCard } from "@/components/customer/balance-card";
import { TransactionFeedItem } from "@/components/customer/transaction-feed-item";
import { WalletTopUpDialog } from "@/components/customer/wallet-topup-dialog";
import { CardVisual } from "@/components/customer/card-visual";
import { CurrencyConverter } from "@/components/currency-converter";
import { pageTransition } from "@/lib/animations";
import { cn } from "@/lib/utils";

interface VirtualCard {
  id: string;
  lastFour: string | null;
  status: string;
  cardType: string;
  cardBrand: string | null;
  nickname: string | null;
  expMonth: number | null;
  expYear: number | null;
}

interface Transaction {
  id: string;
  type: "CREDIT" | "DEBIT";
  amountCents: number;
  merchantName?: string;
  description?: string;
  createdAt: string;
}

export default function PortalHome() {
  const { customer } = useCustomer();
  const { formatCurrency } = useOrgSettings();
  const [showTopUpDialog, setShowTopUpDialog] = useState(false);

  const { data: cards = [] } = useQuery<VirtualCard[]>({
    queryKey: ["customer-cards"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/customer/cards");
      if (!response.ok) throw new Error("Failed to fetch cards");
      return response.json();
    },
  });

  const { data: transactions = [] } = useQuery<Transaction[]>({
    queryKey: ["customer-transactions-feed"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/customer/wallet/transactions?limit=20");
      if (!response.ok) throw new Error("Failed to fetch transactions");
      return response.json();
    },
  });

  const walletBalance = parseFloat(customer?.walletBalance || "0") * 100;
  
  // Calculate this month stats
  const thisMonthStart = new Date();
  thisMonthStart.setDate(1);
  thisMonthStart.setHours(0, 0, 0, 0);
  
  const thisMonthTransactions = transactions.filter(
    t => new Date(t.createdAt) >= thisMonthStart
  );
  
  const monthlySpent = thisMonthTransactions
    .filter(t => t.type === "DEBIT")
    .reduce((sum, t) => sum + t.amountCents, 0);
    
  const monthlyAdded = thisMonthTransactions
    .filter(t => t.type === "CREDIT")
    .reduce((sum, t) => sum + t.amountCents, 0);

  return (
    <motion.div
      variants={pageTransition}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen"
    >
      {/*
        Single grid rather than a feed + sidebar pair, so the converter can
        sit in the right rail on wide screens while keeping its place in the
        mobile reading order (right after the balance and month summary).
        Every feed item is pinned to column 1; the converter takes column 2
        and spans the full row set so the feed drives row heights.
      */}
      <div className="mx-auto p-4 pb-24 max-w-2xl xl:max-w-6xl">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px] xl:grid-rows-[repeat(4,auto)] xl:items-start">
          {/* Balance Card */}
          <BalanceCard
            balance={walletBalance}
            onTopUp={() => setShowTopUpDialog(true)}
            onSend={() => {}}
            className="xl:col-start-1"
          />

          {/* This Month Stats */}
          <Card className="xl:col-start-1">
            <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">This Month</h3>
              <Badge variant="secondary">
                {thisMonthTransactions.length} transactions
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <ArrowDownCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Added</p>
                  <p className="font-semibold text-green-600">{formatCurrency(monthlyAdded)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center">
                  <ArrowUpCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Spent</p>
                  <p className="font-semibold text-red-600">{formatCurrency(monthlySpent)}</p>
                </div>
              </div>
            </div>
            </CardContent>
          </Card>

          {/* Currency Converter — right rail on xl, inline third on mobile */}
          <CurrencyConverter
            surface="portal"
            className="xl:col-start-2 xl:row-start-1 xl:row-span-full xl:sticky xl:top-4"
          />

          {/* My Cards - Horizontal Scroll */}
          {cards.length > 0 && (
            <div className="xl:col-start-1">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg">My Cards</h3>
              <Link href="/portal/cards">
                <Button variant="ghost" size="sm">
                  View All
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="flex gap-8 overflow-x-auto pb-2 px-1 scrollbar-hide">
              {cards.map((card, index) => (
                <Link key={card.id} href={`/portal/cards/${card.id}`}>
                  <div className="flex-shrink-0 w-64">
                    <CardVisual
                      cardNumber={card.lastFour || "****"}
                      cardHolder={`${customer?.firstName || ""} ${customer?.lastName || ""}`.trim()}
                      expMonth={card.expMonth || undefined}
                      expYear={card.expYear || undefined}
                      status={card.status}
                      nickname={card.nickname || undefined}
                      cardType={card.cardBrand || card.cardType}
                      variant={(index % 2) + 1 as 1 | 2}
                      size="sm"
                    />
                  </div>
                </Link>
              ))}
              <Link href="/portal/cards">
                <div className="flex-shrink-0 w-64 h-40">
                  <Card className="border-2 border-dashed hover:border-primary/50 transition-all h-full">
                    <CardContent className="flex flex-col items-center justify-center h-full">
                      <Plus className="h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-sm font-medium">Request New Card</p>
                    </CardContent>
                  </Card>
                </div>
              </Link>
            </div>
          </div>
        )}

        {/* Recent Activity Feed */}
        <div className="xl:col-start-1">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-lg">Recent Activity</h3>
            <Link href="/portal/transactions">
              <Button variant="ghost" size="sm">
                View All
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
          
          {transactions.length > 0 ? (
            <Card>
              <CardContent className="p-0">
                {transactions.slice(0, 10).map((txn) => (
                  <TransactionFeedItem
                    key={txn.id}
                    id={txn.id}
                    type={txn.type}
                    amount={txn.amountCents}
                    merchantName={txn.merchantName}
                    description={txn.description}
                    createdAt={txn.createdAt}
                  />
                ))}
                <Link href="/portal/transactions">
                  <Button variant="ghost" className="w-full rounded-none">
                    View All Transactions
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <TrendingUp className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No Activity Yet</h3>
                <p className="text-muted-foreground text-center mb-4">
                  Start by adding money to your wallet
                </p>
                <Button onClick={() => setShowTopUpDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Top Up Now
                </Button>
              </CardContent>
            </Card>
          )}
          </div>
        </div>
      </div>

      <WalletTopUpDialog
        open={showTopUpDialog}
        onOpenChange={setShowTopUpDialog}
      />

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </motion.div>
  );
}
